import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { memoryApi } from "../services/api";
import { extractList } from "./utils";

/**
 * Self-contained fetch hook for the paginated Memories list.
 *
 * Mirrors useTasks: the component owns the filter/pagination UI state and
 * passes it in; the hook owns fetching, aborting stale requests, and the
 * loading/error lifecycle.
 *
 * @param {{
 *   page: number,
 *   pageSize: number,
 *   search: string,
 *   state: string,        // "all" | "learning" | "review" | "relearning"
 *   sortBy: string,
 *   sortOrder: string,    // "asc" | "desc"
 *   enabled?: boolean,
 * }} options
 */
const useMemories = ({
  page,
  pageSize,
  search,
  state,
  sortBy,
  sortOrder,
  enabled = true,
}) => {
  const [memories, setMemories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which page `error` came from: the list moves its page a render before
  // the refetch clears the error, and must know the error is the old
  // page's, not the new one's (board #92).
  const [errorPage, setErrorPage] = useState(null);
  // Bumped by refresh(); folded into the effect deps so a manual refresh runs
  // through the SAME AbortController-managed effect as a param-driven fetch.
  // Calling the fetcher directly would dodge that abort wiring and let a slow
  // refresh resolve after — and clobber — a newer paging fetch.
  const [refreshKey, setRefreshKey] = useState(0);

  const params = useMemo(() => {
    const query = {
      page,
      page_size: pageSize,
      ordering: sortOrder === "desc" ? `-${sortBy}` : sortBy,
    };
    if (search) query.search = search;
    if (state && state !== "all") query.state = state;
    return query;
  }, [page, pageSize, search, state, sortBy, sortOrder]);

  const fetchMemories = useCallback(
    async (signal) => {
      if (!enabled) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await memoryApi.getAll(params, { signal });
        setMemories(extractList(response.data));
        setTotalCount(response.data.count ?? extractList(response.data).length);
      } catch (err) {
        // A newer request superseded this one; let it drive the state
        if (axios.isCancel(err)) return;
        setError(err);
        setErrorPage(params.page);
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [params, enabled],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchMemories(controller.signal);
    return () => controller.abort();
  }, [fetchMemories, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  return { memories, totalCount, loading, error, errorPage, refresh };
};

export default useMemories;
